import React, { useState, useEffect } from "react";
import i18n from "i18next";
import { Grid, Typography } from "@mui/material";
import { makeValidate } from "mui-rff";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { FORM_ERROR } from "final-form";
import {
  PageBox,
  PageHeader,
  FormAlert,
  FormTextField,
  FormButton,
  DataGrid,
  DataCollapse
} from "./Transactions.style";
import {  GridColDef } from '@mui/x-data-grid';
import { TransactionsSchema } from "../../schema/TransactionsSchema";
import { TabTitle } from "../../utils/GeneralFunctions";
import { useTransactions } from "../../providers/TransactionsProvider";
import { TransactionQueryInterface } from "../../interfaces/TransactionQueryInterface";
import { TransactionDataInterface } from "../../interfaces/TransactionDataInterface";
import { useAuth } from "../../providers/Auth";


const Transactions = () => {
  const auth = useAuth();
  const transactions = useTransactions();
  const { t } = useTranslation();
  const [severity, setSeverity] = useState("success");
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const [showData, setShowData] = useState(false);
  const [rows, setRows] = useState<TransactionDataInterface[]>([]);

  if (auth){
    auth.refreshSession();
  }

  const validate = makeValidate(TransactionsSchema, (error) => {
    return i18n.t(error.message);
  });

  // COLUMN DECLARATION
  const columns: GridColDef[] = [
    { field: 'id', headerName: "Id", minWidth: 80, hide: true },
    { field: 'activityDate', headerName: t("transactions.list.headers.activity.date"), minWidth: 280, disableColumnMenu: true},
    { field: 'mpc', headerName: t("transactions.list.headers.mpc"), minWidth: 80, disableColumnMenu: true},
    { field: 'mpcDesc', headerName: t("transactions.list.headers.mpc.description"), minWidth: 200, disableColumnMenu: true },
    { field: 'creditingDate', headerName: t("transactions.list.headers.crediting.date"), minWidth: 280 , disableColumnMenu: true},
    { field: 'points', headerName: t("transactions.list.headers.points"), minWidth: 200 , disableColumnMenu: true},
  ];

  // PACTOOL - Search and Validate
  TabTitle(t("app.name") + " - " + t("transactions.page.title"));

  let formData = {
    aeroplanNumber: "",
    lastName: ""
  };

  const onSubmit = async (values) => {
    setSubmitLoading(true);

    const query = {
      memberId: values.aeroplanNumber,
      lastName: values.lastName,
    } as TransactionQueryInterface;

    const result: any = await transactions.fetchTransactions(query);

    setSubmitLoading(false);
    if (result.status === "FAIL" || result.error) {
      setSeverity("error");
      return { [FORM_ERROR]: result.message };
    }
    if (result.status === "SUCCESS") {
      setSeverity("success");
      if (result.count > 0) {
        return {[FORM_ERROR]: "transactions.block-form.error.validation.success"};
      } else {
        return {[FORM_ERROR]: "transactions.block-form.error.validation.success.empty"};
      }
    } else {
      setSeverity("error");
      return { [FORM_ERROR]: "transactions.block-form.error.validation.failed" };
    }
  };

  useEffect(() => {
    if (!transactions.loading) {
      if (transactions.transactionsData?.length === 0) {
        setShowData(false);
        setRows([]);
      } else {
        setShowData(true);
        setRows(transactions.transactionsData);
      }
    }
    // eslint-disable-next-line
  }, [
    transactions.transactionsData,
    transactions.loading
  ]);

  return (
    <>
      <PageBox>
        <Grid container sx={{ pt: 0 }}>
          <Grid item xs={12} sm={6}>
            <PageHeader>{t("transactions.page.title")}</PageHeader>
            <Typography
              component="p"
              variant="lead"
              sx={{ fontSize: "1.0em", marginBottom: "2rem" }}
            >
              {t("transactions.page.description")}
            </Typography>
            <Form
              onSubmit={onSubmit}
              initialValues={formData}
              validate={validate}
              render={({
                submitError,
                handleSubmit,
                form,
                submitting,
                pristine
              }) => (
                <form onSubmit={handleSubmit}>

                  {submitError && (
                    <FormAlert severity={severity}>{t(submitError)}</FormAlert>
                  )}

                  <Typography component="p" variant="lead">
                    {t("transactions.block-form.title")}
                  </Typography>

                  <FormTextField
                    name="aeroplanNumber"
                    variant="filled"
                    InputProps={{ disableUnderline: true }}
                    label={t("transactions.block-form.field.aeroplannumber")}
                    id="aeroplanNumber"
                  />

                  <FormTextField
                    name="lastName"
                    variant="filled"
                    InputProps={{ disableUnderline: true }}
                    label={t("transactions.block-form.field.lastname")}
                    id="lastName"
                  />

                  <FormButton
                    type="button"
                    onClick={() => {
                      form.reset();
                      setShowData(false);
                  }}
                    disabled={submitting || pristine}
                  >
                    {t("transactions.block-form.button.clear")}
                  </FormButton>

                  <FormButton 
                    type="submit"
                    loadingPosition="end"
                    loading={submitLoading}
                    endIcon={<div />} 
                    disabled={submitting}>
                    {t("transactions.block-form.button.submit")}
                  </FormButton>

                </form>
              )}
            />
          </Grid>
        </Grid>
        <DataCollapse in={showData}>
          <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
        />
      </DataCollapse>
      </PageBox>
    </>
  );
};

export default Transactions;
